import { useQuery } from "@pinia/colada";
import { max } from "radash";
import { objectToSnake } from "ts-case-convert";
import { computed, reactive } from "vue";

import type {
  I18nFactory,
  I18nParams,
  LocaleMorpheus,
} from "@solvari/translations";
import type { ToMaybeRefs } from "@solvari/utils";

import { capitalizeFirst } from "@solvari/common-fe/helpers";

import {
  useMunicipality,
  useProvince,
} from "~/features/company-wall/api/geo.api.ts";
import { useGeoProductCategory } from "~/features/company-wall/api/localSearch.api.ts";
import { useCmsLocalSearch } from "~/features/company-wall/api/localSearchCms.api.ts";

type TranslationParamValue = I18nParams[string];

function omitNil(
  params: Record<string, TranslationParamValue | null | undefined>,
) {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  ) as Record<string, TranslationParamValue>;
}

function getLongestUnderLimit(values: string[], limit: number) {
  return max(values, (value) => (value.length <= limit ? value.length : 0));
}

function metaTitleFallback(options: string[]) {
  return getLongestUnderLimit(options, 65);
}

function metaDescriptionFallback(options: string[]) {
  // Make sure that no variables remain in the translated value
  const substituted = options.filter((option) => !/{[^}]+}/.test(option));
  return getLongestUnderLimit(
    substituted.length > 0 ? substituted : options,
    156,
  );
}

function useLocalSearchTranslations<Keys extends string>(
  useI18n: I18nFactory<Keys>,
  keyParams: ToMaybeRefs<{
    geoProductSlug: string;
    localeCode: LocaleMorpheus;
    localReviewCount?: number;
    municipality?: string;
    province?: string;
  }>,
) {
  const params = reactive(keyParams);

  const { data: geoProductCategory } = useQuery(
    useGeoProductCategory({
      slug: params.geoProductSlug,
      localeCode: params.localeCode,
    }),
  );

  const { data: cmsLocalSearch } = useQuery(
    useCmsLocalSearch({
      uri: `/${params.geoProductSlug}`,
      locale: params.localeCode,
    }),
  );

  const { data: province } = useQuery({
    ...useProvince({
      slug: params.province!,
      localeCode: params.localeCode,
    }),
    enabled: () => !!params.province,
  });

  const { data: municipality } = useQuery({
    ...useMunicipality({
      slug: params.municipality!,
      localeCode: params.localeCode,
    }),
    enabled: () => !!params.municipality,
  });

  const baseParams = computed(() =>
    objectToSnake(
      omitNil({
        province: province.value?.name ?? params.province,
        municipality: municipality.value?.name ?? params.municipality,
        productSg:
          cmsLocalSearch.value?.productSingular ?? params.geoProductSlug,
        productPl: cmsLocalSearch.value?.productPlural ?? params.geoProductSlug,
        customerSg: cmsLocalSearch.value?.customerSingular,
        customerPl: cmsLocalSearch.value?.customerPlural,
        totalCustomers: geoProductCategory.value?.totalCustomers,
        totalReviews: geoProductCategory.value?.totalReviews,
        localReviewCount: params.localReviewCount,
        reviewScore: geoProductCategory.value?.averageRating,
        totalLeads: geoProductCategory.value?.totalLeads,
      }),
    ),
  );

  return useI18n({ baseParams });
}

function seoWithCmsFallback(
  cmsValues: string[] | undefined,
  fallback: () => string[],
  interpolate: (value: string) => string,
  select: (options: string[]) => string | null | undefined,
): string | undefined {
  const options = cmsValues?.length ? cmsValues.map(interpolate) : fallback();

  const result = select(options);
  return result ? capitalizeFirst(result) : undefined;
}

export {
  metaDescriptionFallback,
  metaTitleFallback,
  seoWithCmsFallback,
  useLocalSearchTranslations,
};
